import { ref } from 'vue';
import { nexusApi } from '@/service/nexus';

class VersionCheckService {
    constructor() {
        this.updateAvailable = ref(false);
        this.newVersion = ref(null);
        this.currentVersion = ref(null);
        this.checkInterval = null;
        this.buildHash = null;
        this.isChecking = false;
        this.initialized = false;
    }

    async initialize() {
        if (this.initialized) return;
        this.initialized = true;
        // Get initial build hash from meta tag
        this.buildHash = document.querySelector('meta[name="build-hash"]')?.content;

        // Start periodic meta tag checks
        this.startMetaTagPolling();

        // Check version via API
        await this.checkVersionViaAPI();
    }

    /**
     * Check version via API endpoint
     */
    async checkVersionViaAPI() {
        try {
            const response = await nexusApi.get('/core/version/');
            const versionInfo = response.data;

            if (!this.currentVersion.value) {
                // First check - store current version
                this.currentVersion.value = versionInfo.version;
                localStorage.setItem('app-version', versionInfo.version);
                localStorage.setItem('app-build-hash', versionInfo.build_hash);
            } else if (this.currentVersion.value !== versionInfo.version) {
                // Version changed
                this.newVersion.value = versionInfo.version;
                this.updateAvailable.value = true;
            }
        } catch (error) {
            console.error('Failed to check version via API:', error);
        }
    }

    /**
     * Start polling for meta tag changes
     */
    startMetaTagPolling() {
        // Check every 5 minutes
        this.checkInterval = setInterval(
            () => {
                this.checkMetaTagHash();
            },
            5 * 60 * 1000
        );

        // Also check when page becomes visible
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden && !this.isChecking) {
                this.checkMetaTagHash();
            }
        });
    }

    /**
     * Check if build hash in meta tag has changed
     */
    async checkMetaTagHash() {
        if (this.isChecking || !this.buildHash) return;

        this.isChecking = true;

        try {
            // Fetch the current page HTML
            const response = await fetch(window.location.href, {
                method: 'GET',
                cache: 'no-cache'
            });

            if (response.ok) {
                const html = await response.text();
                const match = html.match(/<meta name="build-hash" content="(.+?)"/);

                if (match && match[1] && match[1] !== this.buildHash) {
                    console.log('New build hash detected:', match[1]);
                    this.buildHash = match[1];
                    this.updateAvailable.value = true;

                    // Also check version via API
                    await this.checkVersionViaAPI();
                }
            }
        } catch (error) {
            console.error('Failed to check meta tag hash:', error);
        } finally {
            this.isChecking = false;
        }
    }

    /**
     * Reload the application
     */
    reload() {
        // Clear caches and reload
        if ('caches' in window) {
            caches.keys().then((names) => {
                names.forEach((name) => caches.delete(name));
            });
        }

        // Force reload bypassing cache
        window.location.reload(true);
    }

    /**
     * Dismiss the update notification
     */
    dismiss() {
        this.updateAvailable.value = false;

        // Store dismissed version to avoid repeated notifications
        if (this.newVersion.value) {
            sessionStorage.setItem('dismissed-version', this.newVersion.value);
        }
    }

    /**
     * Check if update was already dismissed in this session
     */
    isUpdateDismissed() {
        const dismissedVersion = sessionStorage.getItem('dismissed-version');
        return dismissedVersion === this.newVersion.value;
    }

    /**
     * Cleanup resources
     */
    destroy() {
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
            this.checkInterval = null;
        }

        this.initialized = false;
    }
}

// Create singleton instance
export const versionCheckService = new VersionCheckService();

// Export composable for Vue components
export function useVersionCheck() {
    return {
        updateAvailable: versionCheckService.updateAvailable,
        newVersion: versionCheckService.newVersion,
        currentVersion: versionCheckService.currentVersion,
        reload: () => versionCheckService.reload(),
        dismiss: () => versionCheckService.dismiss()
    };
}
